<template>
  <div
    v-on-clickaway="hideDropdown"
    class="container-datepicker"
    :class="classes"
  >
    <!-- FIELD -->
    <div
      class="datepicker__field-wrapper"
      @click="toggleDropdownVisibility"
    >
      <label
        v-if="label && !date"
        class="datepicker__label"
      >
        {{ label }}
        <span v-if="required" class="datepicker__label-required">*</span>
      </label>
      <div class="datepicker__field">
        <div :class="['datepicker__text', { 'datepicker__text--centered': date }]">
          <span class="datepicker__date">{{ formatDate(date) }}{{ time && time.length ? ',' : '' }}</span>
          <span class="datepicker__time">{{ time }}</span>
        </div>
        <CalendarIcon class="datepicker__arrow" />

      </div>
    </div>
    <!-- FIELD -->

    <!-- DROPDOWN -->
    <transition name="slide-from-top-short">
      <div
        v-if="isDropdownVisible"
        class="datepicker__dropdown"
      >
        <!-- CALENDAR -->
        <transition :name="dropdownInsideTransitionName" mode="out-in">
          <div
            v-if="!isPickingTime"
            key="date-picker"
            class="datepicker__dropdown-date"
          >
            <datepicker
              :value="date"
              v-bind="datepickerOptions"
              @input="onDateChange"
            />
            <div class="datepicker__dropdown-button-container">
              <BaseButton
                class="datepicker__dropdown-button"
                type="secondary"
                @click="onPickTime"
              >
                {{ buttonsText.pickTime }}
              </BaseButton>
            </div>
          </div>
          <!-- CALENDAR -->

          <!-- TIME -->
          <div
            v-else
            key="time-picker"
            class="datepicker__dropdown-time"
          >
            <div class="datepicker__dropdown-time-title">{{ formatDate(date) }}</div>
            <div class="datepicker__dropdown-times">
              <div
                v-for="(timeItem, index) in getAvailableTimes(date)"
                :key="`time-item-${index}`"
                :class="[
                  'datepicker__dropdown-time-item',
                  { 'datepicker__dropdown-time-item_disabled': timeItem.disabled },
                  { 'datepicker__dropdown-time-item_selected': isTimeSelected(timeItem) }
                ]"
                @click="selectTime(timeItem)"
              >
                {{ timeItem.value }}
              </div>
            </div>
            <div class="datepicker__dropdown-button-container">
              <div
                class="datepicker__dropdown-back-to-calendar-button"
                @click="isPickingTime = false"
              >
                <ChevronIcon class="datepicker__dropdown-back-to-calendar-button-arrow" />
                <div class="datepicker__dropdown-back-to-calendar-button-text">{{ buttonsText.backToCalendar }}</div>
              </div>
            </div>
            <div class="datepicker__dropdown-button-container">
              <BaseButton
                class="datepicker__dropdown-button"
                type="secondary"
                @click="hideDropdown"
              >
                {{ buttonsText.submit }}
              </BaseButton>
            </div>
          </div>
          <!-- TIME -->
        </transition>
      </div>
    </transition>
    <!-- DROPDOWN -->

    <transition name="slide-from-top">
      <span
        v-if="error && errorMsg"
        class="datepicker__error-msg"
      >
        {{ errorMsg }}
        <ErrorIcon/>
      </span>
    </transition>
  </div>
</template>

<script>
  import { mapState } from 'vuex'
  import { format, getDay, differenceInMinutes } from 'date-fns'
  import * as uk from 'date-fns/locale/uk/index.js'
  import * as ro from 'date-fns/locale/ro/index.js'
  import * as ru from 'date-fns/locale/ru/index.js'
  import * as enUS from 'date-fns/locale/en-US/index.js'
  import { uk as datapickerUk, ro as datapickerRo, ru as datapickerRu, en as datapickerEn } from 'vuejs-datepicker/dist/locale'
  import Datepicker from 'vuejs-datepicker'
  import CalendarIcon from '@/assets/img/icons/calendar.svg?inline'
  import ChevronIcon from '@/assets/img/icons/chevron.svg?inline'
  import ErrorIcon from '@/assets/img/icons/error.svg?inline'
  import { mixin as clickaway } from 'vue-clickaway'
  import { cloneDeep, get } from 'lodash'

  export default {
    name: 'BaseDatePickerNew',
    components: {
      Datepicker,
      CalendarIcon,
      ChevronIcon,
      ErrorIcon
    },
    mixins: [ clickaway ],
    props: {
      globalTime: String,
      changeTime: Function,
      requestServiceIn: Number,
      workPeriod: [Function, Object],
      label: {
        type: String,
        default: ''
      },
      errorMsg: {
        type: String,
        default: ''
      },
      required: {
        type: Boolean,
        default: false
      },
      buttonsText: {
        type: Object,
        required: true,
        validator (buttonsText) {
          return 'pickTime' in buttonsText &&
            'submit' in buttonsText &&
            'backToCalendar' in buttonsText
        }
      },
      options: {
        type: [Object],
        required: true,
        default: () => ({})
      }
    },

    data () {
      return {
        date: null,
        time: '',
        datesWithNoAvailableTime: [],
        isDropdownVisible: false,
        isPickingTime: false,
        error: null,
        datapickerLang: datapickerUk,
        timepickerLang: uk
      }
    },

    computed: {
      ...mapState(['currentCountry', 'currentLang']),
      classes () {
        return [
          'datepicker',
          {
            'datepicker_active': this.isDropdownVisible,
            'datepicker_error': this.error
          }
        ]
      },
      datepickerOptions () {
        return {
          'monday-first': true,
          'full-month-name': true,
          'maximum-view': 'day',
          'use-utc': true,
          'inline': true,
          'language': this.datapickerLang,
          'format': this.formatDate(),
          'disabledDates': {
            to: new Date(new Date().setDate(new Date().getDate() + this.requestServiceIn)),
            days: this.disabledWeekDays,
            dates: [...this.datesWithNoAvailableTime, new Date()]
          }
        }
      },
      dropdownInsideTransitionName () {
        return this.isPickingTime
          ? 'slide-to-left'
          : 'slide-to-right'
      },
      dateTime () {
        // resulting timestamp (emiting value from component)
        if (!this.date || !this.time) return null
        const date = cloneDeep(this.date)
        const hours = Number(this.time.split(':')[0])
        const minutues = Number(this.time.split(':')[1])
        date.setHours(hours, minutues, 0, 0)
        return date.getTime()
      },
      disabledWeekDays () {
        // format to US day style
        // from 0 = Monday, 6 = Sunday
        // to 0 = Sunday, 6 = Saturday
        if (!this.workPeriod || !this.workPeriod.disabled) return []
        return this.workPeriod.disabled.map(dayIndex => {
          return dayIndex === 6
            ? 0
            : dayIndex + 1
        })
      }
    },

    watch: {
      errorMsg: {
        handler (msg) {
          this.error = !!msg
        },
        immediate: true
      },
      globalTime: {
        handler (globalTime) {
          if (!globalTime.length) {
            this.date = null
            this.time = ''
            this.datesWithNoAvailableTime = []
          }
        }
      }
    },

    created () {
      if (this.currentLang === 'ro') {
        this.datapickerLang = datapickerRo
        this.timepickerLang = ro
      } else if (this.currentLang === 'ru') {
        this.datapickerLang = datapickerRu
        this.timepickerLang = ru
      } else if (this.currentLang === 'uk') {
        this.datapickerLang = datapickerUk
        this.timepickerLang = uk
      } else if (this.currentLang === 'en') {
        this.datapickerLang = datapickerEn
        this.timepickerLang = enUS
      } else {
        if (this.currentCountry === 'md') {
          this.datapickerLang = datapickerRo
          this.timepickerLang = ro
        } else if (this.currentCountry === 'ru') {
          this.datapickerLang = datapickerRu
          this.timepickerLang = ru
        }
      }
    },

    methods: {
      initDatePicker () {
        // select first available date and time
        this.onDateChange(this.getFirstAvailableDate())
      },
      getFirstAvailableDate () {
        const today = new Date()
        const tomorrow = new Date()
        tomorrow.setDate(today.getDate() + 1)

        let availableDate = tomorrow

        while (!this.getFirstAvailableTime(availableDate)) {
          const thisDay = cloneDeep(availableDate)
          const nextDay = cloneDeep(thisDay)
          nextDay.setDate(thisDay.getDate() + 1)

          // disable day on calendar if it has no available time
          this.datesWithNoAvailableTime = [...this.datesWithNoAvailableTime, cloneDeep(thisDay)]

          availableDate = cloneDeep(nextDay)
        }

        return availableDate
      },
      getFirstAvailableTime (date) {
        const availableTime = this.getAvailableTimes(date).find(time => {
          return !('disabled' in time) || !time.disabled
        })

        return get(availableTime, 'value')
      },
      getAvailableTimes (date) {
        const selectedWeekDayIndex = getDay(date) === 0
          ? 6
          : getDay(date) - 1

        const isToday = () => {
          return date.getFullYear() === new Date().getFullYear() &&
            date.getMonth() === new Date().getMonth() &&
            date.getDate() === new Date().getDate()
        }

        if (!this.workPeriod || !this.workPeriod.enabled) return []

        if (isToday()) {
          // disable already passed times
          return this.workPeriod.enabled[selectedWeekDayIndex]
            ? this.workPeriod.enabled[selectedWeekDayIndex]
              .map(time => {
                const timeToCompare = new Date()
                const hours = Number(time.value.split(':')[0])
                const minutues = Number(time.value.split(':')[1])
                timeToCompare.setHours(hours, minutues, 0, 0)

                const isTimePass = differenceInMinutes(timeToCompare, new Date()) < 0

                return isTimePass
                  ? { ...time, disabled: true }
                  : time
              })
            : []
        }

        return this.workPeriod.enabled[selectedWeekDayIndex] || []
      },

      formatTime (date) {
        return format(date, 'HH:mm', { locale: this.timepickerLang })
      },
      formatDate (date) {
        if (!date) return ''
        return format(date, 'd MMMM', { locale: this.timepickerLang })
      },

      onDateChange (date) {
        this.date = date
        if (this.changeTime) this.changeTime(this.time + ', ' + this.formatDate(date))
        this.$emit('input', this.dateTime)
        this.onTimeChange(this.getFirstAvailableTime(date), false)
        this.isPickingTime = true
      },
      onTimeChange (time, check = true) {
        this.time = time || ''
        if (this.changeTime) this.changeTime(time + ', ' + this.formatDate(this.date))
        this.$emit('input', this.dateTime)
        if (check) {
          setTimeout(() => {
            this.hideDropdown()
          }, 0)
        }
      },

      onPickTime () {
        this.isPickingTime = true
      },
      isTimeSelected (time) {
        return this.time === time.value
      },
      selectTime (time) {
        if (time.disabled) return
        this.onTimeChange(time.value)
      },

      toggleDropdownVisibility () {
        this.error = false
        this.isDropdownVisible
          ? this.hideDropdown()
          : this.showDropdown()
      },
      showDropdown () {
        this.isPickingTime = false
        this.isDropdownVisible = true
      },
      hideDropdown () {
        this.isDropdownVisible = false
        this.isPickingTime = false
      }
    }
  }
</script>

<style lang="scss" scoped>
  $padding-for-error: 28px;

  .container-datepicker {
    margin-top: auto;
  }

  .datepicker {
    position: relative;
    display: block;
    align-self: flex-start;
    min-width: 290px;
    // max-width: 335px;
    padding-bottom: $padding-for-error;
    cursor: pointer;
    margin-top: 0;

    @include mobile {
      max-width: 100%;
    }

    &.disabled {
      pointer-events: none;

      .datepicker__field {
        background: $c-gray-4;
      }

      .datepicker__text,
      .datepicker__date {
        color: $c-gray-7;
      }
    }

    &__field-wrapper {
      position: relative;
    }

    &__label {
      user-select: none;
      pointer-events: none;
      position: absolute;
      z-index: 1;
      top: 50%;
      left: 18px;
      transform: translateY(-50%);
      display: inline-block;
      font-family: "ToyotaType-Book", sans-serif;
      font-size: 16px;
      line-height: 1.2;
      color: $c-gray-7;
      transition: 0.3s;
      letter-spacing: 0.2px;

      &-required {
        position: relative;
        top: -3px;
        color: $c-errorDark !important;
        font-size: 13px;
        right: -3px;
      }

      &-top {
        top: 11px;
        font-size: 13px;
        transform: translateY(0);

        @include mobile {
          font-size: 11px;
        }
      }
    }

    &__field {
      position: relative;
      display: block;
      width: 100%;
      min-height: 60px;
      padding: 17px 19px 17px 16px;
      font-family: "ToyotaType-Book", Arial, Helvetica, sans-serif;
      font-size: 16px;
      line-height: 1;
      background-color: $c-white;
      border: 1px solid $c-gray-4;
      outline: none;
      caret-color: $c-active-light;
      transition: 0.3s;
      appearance: none !important;
      -webkit-appearance: none !important;
      border-radius: 0 !important;

      &:focus {
        background-color: $c-gray-4;
      }

      @include hover {
        background-color: $c-gray-1;

        + .datepicker__label {
          color: $c-gray-dark;
        }
      }
    }

    &__text {
      position: relative;
      top: 9px;
      padding-right: 20px;
      color: $c-gray-7;
      line-height: 1.5;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;

      &--centered {
        top: 0;
      }
    }

    &__arrow {
      position: absolute;
      right: 18px;
      top: 50%;
      width: 20px;
      height: 20px;
      transition: 0.3s;
      transform: translateY(-50%) !important;
      transform-origin: top;
    }

    &__time {
      color: $c-gray-7;
    }

    &__date {
      margin-right: 6px;
      font-family: "ToyotaType-Book", Arial, Helvetica, sans-serif;
      color: $c-gray-7;
    }

    &__dropdown {
      //max-width: 338px;
      padding: 15px 10px 0;
      font-family: "ToyotaType-Book", Arial, Helvetica, sans-serif;
      cursor: default;
      user-select: none;
      border-radius: 4px;
      position: absolute;
      top: 60px;
      left: 0;
      z-index: 999;
      max-width: 365px;
      width: 100%;
      background: $c-gray-1;
      transition: 0.3s;
      -webkit-overflow-scrolling: touch;
      //border-bottom: 1px solid $c-gray-4;

      @include mobile {
        right: 0;
        padding: 5px 0;
      }

      &-button {
        border-radius: 0;

        &-container {
          display: flex;
          justify-content: center;
          margin: 20px 0;
        }

        background-color: $c-white;
        transition: all 0.4s;

        &:hover {
          background-color: $c-black !important;
          color: $c-white !important;
          // box-shadow: 0 2px 10px 0 $c-gray-medium;
        }
      }

      &-time {
        padding-right: 10px;
        padding-left: 10px;

        &-title {
          margin: 10px 0 25px;
          text-align: center;
        }
      }

      &-times {
        display: flex;
        flex-wrap: wrap;
        margin-top: 10px;
      }

      &-time-item {
        flex: 0 1 20%;
        margin-bottom: 25px;
        color: $c-gray-dark;
        text-align: center;
        cursor: pointer;
        transition: color 0.3s;

        &:hover {
          color: $c-active;
        }

        &_disabled {
          color: $c-gray-medium;
          pointer-events: none;
        }

        &_selected {
          color: $c-active;
        }
      }

      &-back-to-calendar-button {
        display: flex;
        align-items: center;
        margin-right: 5px;
        cursor: pointer;

        &-arrow {
          width: 16px;
          height: 16px;
          margin-right: 10px;
          transition: 0.3s;
        }

        &-text {
          font-size: 12px;
          line-height: 16px;
          transition: color 0.3s;
        }

        &:hover {
          .datepicker__dropdown-back-to-calendar-button {
            &-text {
              color: $c-active;
            }

            &-arrow {
              stroke: $c-active;
            }
          }
        }
      }
    }

    &__error-msg {
      display: flex;
      align-items: center;
      justify-content: space-between;
      position: absolute;
      left: 0;
      font-size: 12px;
      color: $c-errorDark;
      height: 20px;
      bottom: 4px;
      width: 100%;

      img,
      svg {
        display: flex;
        min-width: 14px;
        min-height: 15px;
        width: 14px;
        height: 15px;
        object-fit: contain;
      }
    }

    &_error {
      .datepicker {
        &__field {
          border: 1px solid $c-errorDark;
        }
      }
    }

    &_active {
      .datepicker {
        &__field {
          background-color: $c-gray-1 !important;
        }

        &__arrow {
          transform: rotate(0deg) translateY(-50%) !important;
        }

        &__label {
          top: 11px;
          font-size: 13px;
          transform: translateY(0);
          color: $c-gray-dark;

          @include mobile {
            font-size: 11px;
          }
        }
      }
    }
  }
</style>

<style lang="scss">
  .container-datepicker .datepicker__dropdown .vdp-datepicker__calendar {
    width: 100%;
    padding-left: 4px;
    margin: 0 auto;
    font-family: "ToyotaType-Book", Arial, Helvetica, sans-serif;
    background: transparent;
    border: none;

    @include mobile {
      padding-left: 27px;
      transform: translateX(-13px);
    }

    @include mobileXs {
      padding-left: 0;
      transform: translateX(1px);
    }

    header {
      display: flex;
      justify-content: space-between;
      padding: 0 15%;

      .day__month_btn.up {
        transition: color 0.3s;

        &:hover {
          color: $c-active;
          background: transparent;
        }
      }
    }

    .prev,
    .next {
      overflow: hidden;
      background: url('../../assets/img/icons/chevron.svg') no-repeat center;
      background-size: 15px;
      opacity: 0.7;
      transition: background 0.3s, opacity 0.3s;
      transform: rotate(-180deg);

      &::after {
        display: none;
      }

      &:hover:not(.disabled) {
        background: url('../../assets/img/icons/chevron-red.svg') no-repeat center;
        background-size: 15px;
        opacity: 1;
      }

      &.disabled {
        opacity: 0.2;
      }
    }

    .prev {
      transform: rotate(0deg);
    }

    .cell {
      border: none !important;
      transition: all 0.3s;

      &.month {
        &:hover {
          color: $c-active-light;
          background: transparent;
        }
      }

      &.selected {
        color: $c-white !important;
        background: $c-active-light;
      }

      &.weekend {
        &:hover {
          color: $c-white;
        }
      }

      &:hover {
        color: $c-white !important;
        background: $c-black !important;
      }

      &.day {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 14.28%;
        height: unset;
        padding: 0;
        border-radius: 50%;
        aspect-ratio: 1;
        line-height: normal;
        margin: 0;
      }

      &.day-header,
      &.blank {
        color: $c-gray-dark;

        &:hover {
          color: $c-gray-dark;
          cursor: default;
        }
      }

      &.disabled {
        color: $c-gray-medium;
        pointer-events: none;
      }
    }
  }
</style>
